import React, { useState } from 'react';

export default function CyberSecurity() {
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    companySize: '10-50 employees',
    securityFocus: 'Managed Threat Detection (MDR)',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans selection:bg-orange-500 selection:text-white">

      {/* ==========================================
          SECTION 1: HERO / THREAT INTELLIGENCE
         ========================================== */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28 border-b border-orange-100">
        {/* Decorative Background Accents */}
        <div className="absolute top-1/4 left-10 -z-10 w-80 h-80 bg-orange-200/30 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-1/2 right-10 -z-10 w-96 h-96 bg-orange-400/15 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f9731608_1px,transparent_1px),linear-gradient(to_bottom,#f9731608_1px,transparent_1px)] bg-[size:32px_32px]" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Hero Content */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 font-bold text-xs tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Enterprise Cyber Defense
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.1]">
              Protect Your Business <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500">
                Against Advanced Threats
              </span>
            </h1>

            <p className="text-lg text-stone-600 max-w-xl leading-relaxed">
              Shield your critical data, infrastructure, and brand with zero-trust architecture, automated threat mitigation, and 24/7 continuous monitoring.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#assessment-section"
                className="px-8 py-4 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all duration-200 shadow-xl shadow-orange-500/25 hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
              >
                Get Security Audit
              </a>
              <a
                href="#solutions-section"
                className="px-8 py-4 rounded-xl bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-800 font-semibold transition-all duration-200"
              >
                Explore Solutions
              </a>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-orange-100">
              <div>
                <p className="text-3xl font-extrabold text-stone-900">24 / 7</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">SOC Monitoring</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-orange-600">&lt; 5min</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Incident Response</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-stone-900">99.9%</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Threat Block Rate</p>
              </div>
            </div>
          </div>

          {/* Right Live Security Status Card */}
          <div className="lg:col-span-5 relative">
            <div className="bg-stone-900 text-white rounded-3xl p-6 shadow-2xl border border-stone-800 relative">
              <div className="flex items-center justify-between pb-4 border-b border-stone-800">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500 animate-ping" />
                  <span className="text-xs font-mono text-stone-300">Shield Status: ACTIVE</span>
                </div>
                <span className="text-[11px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30 px-2.5 py-0.5 rounded-full">
                  Zero Trust Protocol
                </span>
              </div>

              {/* Threat Feed Mockup */}
              <div className="py-6 space-y-4 font-mono">
                <div className="p-3 bg-stone-950/80 rounded-xl border border-stone-800 text-xs flex justify-between items-center">
                  <span className="text-stone-400">Endpoint Protection</span>
                  <span className="text-emerald-400 font-semibold">1,240 Devices Secured</span>
                </div>

                <div className="p-3 bg-stone-950/80 rounded-xl border border-stone-800 text-xs flex justify-between items-center">
                  <span className="text-stone-400">Ransomware Defense</span>
                  <span className="text-orange-400 font-semibold">Real-Time Isolation</span>
                </div>

                <div className="p-3 bg-stone-950/80 rounded-xl border border-stone-800 text-xs flex justify-between items-center">
                  <span className="text-stone-400">Identity Guard</span>
                  <span className="text-emerald-400 font-semibold">MFA Enforced</span>
                </div>

                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-xs text-stone-400">
                    <span>Threat Neural Filter</span>
                    <span className="text-orange-400 font-bold">99.98% Efficiency</span>
                  </div>
                  <div className="w-full h-2 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[98%]" />
                  </div>
                </div>
              </div>

              <div className="p-3 bg-orange-950/30 rounded-xl border border-orange-500/30 flex items-center justify-between text-xs font-mono">
                <span className="text-stone-300">Compliance Standard</span>
                <span className="text-orange-400 font-bold">ISO 27001 & SOC 2</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ==========================================
          SECTION 2: SECURITY SOLUTIONS & PRACTICE
         ========================================== */}
      <section id="solutions-section" className="py-24 bg-orange-50/40">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-orange-600 font-bold text-xs uppercase tracking-widest bg-orange-100 border border-orange-200 px-3 py-1 rounded-full">
              Full Spectrum Protection
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Advanced Security Solutions
            </h2>
            <p className="text-stone-600 text-base">
              Comprehensive defense layers and operational best practices tailored to prevent, detect, and neutralize digital threats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Managed Threat Detection (MDR)
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Continuous threat hunting and rapid isolation using automated incident detection paired with dedicated security analysts.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Zero Trust Network Access
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Never trust, always verify. Enforce granular identity authentication and adaptive policies across every user and device.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Penetration Testing
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Proactively identify system vulnerabilities with ethical hacking simulations before malicious attackers exploit them.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 001-9.999 5.002 5.002 0 00-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cloud Security Posture
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Secure multi-cloud environments (AWS, Azure, GCP) with misconfiguration tracking and continuous policy enforcement.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Security Awareness Training
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Train your workforce with interactive phishing simulations and security best practices to prevent human error.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Compliance & Governance
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Stay compliant with HIPAA, GDPR, SOC 2, and PCI-DSS through automated readiness tracking and audit reports.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          SECTION 3: BEST PRACTICES & AUDIT FORM
         ========================================== */}
      <section id="assessment-section" className="py-24 bg-white border-t border-orange-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 text-white rounded-3xl p-8 lg:p-14 shadow-2xl relative overflow-hidden">
            
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Best Practices Checklist */}
              <div className="lg:col-span-6 space-y-6">
                <span className="bg-orange-500/10 text-orange-400 border border-orange-500/30 font-semibold text-xs px-3.5 py-1.5 rounded-full uppercase tracking-wider">
                  Cyber Hygiene Framework
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                  Essential Security Best Practices for Businesses
                </h2>
                <p className="text-stone-400 text-base leading-relaxed">
                  Adopting robust cybersecurity practices minimizes exploit risk and keeps operational downtime to zero.
                </p>

                {/* Best Practice List */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-600/30 text-orange-400 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">
                      ✓
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Enforce Multi-Factor Authentication (MFA)</h4>
                      <p className="text-xs text-stone-400">Require secondary hardware or app verification on all account sign-ins.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-600/30 text-orange-400 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">
                      ✓
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Automate Security Patching</h4>
                      <p className="text-xs text-stone-400">Keep operating systems and software current to patch known vulnerabilities.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-600/30 text-orange-400 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">
                      ✓
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Immutable Offsite Backups</h4>
                      <p className="text-xs text-stone-400">Maintain encrypted, isolated backups to recover quickly from ransomware attacks.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-600/30 text-orange-400 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">
                      ✓
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Least-Privilege Authorization</h4>
                      <p className="text-xs text-stone-400">Restrict access permissions strictly to what employees need for their role.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Form */}
              <div className="lg:col-span-6">
                <div className="bg-white text-stone-900 rounded-2xl p-6 sm:p-8 shadow-2xl border border-orange-100">
                  <h3 className="text-2xl font-bold text-stone-900 mb-1">
                    Request a Free Vulnerability Audit
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Connect with our cyber experts for a complimentary security posture review.
                  </p>

                  {isSubmitted ? (
                    <div className="bg-orange-50 border border-orange-200 text-stone-800 p-8 rounded-xl text-center space-y-3">
                      <div className="w-14 h-14 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto text-2xl font-bold shadow-lg shadow-orange-600/30">
                        ✓
                      </div>
                      <h4 className="font-bold text-xl text-stone-900">Assessment Requested!</h4>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        A cybersecurity specialist will analyze your domain details and contact you within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Jane Doe"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Work Email
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="jane@company.com"
                          value={formData.workEmail}
                          onChange={(e) => setFormData({ ...formData, workEmail: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Company Size
                        </label>
                        <select
                          value={formData.companySize}
                          onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="1-10 employees">1-10 employees</option>
                          <option value="10-50 employees">10-50 employees</option>
                          <option value="50-250 employees">50-250 employees</option>
                          <option value="250+ employees">250+ employees</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Primary Security Need
                        </label>
                        <select
                          value={formData.securityFocus}
                          onChange={(e) => setFormData({ ...formData, securityFocus: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="Managed Threat Detection (MDR)">Managed Threat Detection (MDR)</option>
                          <option value="Penetration Testing & Auditing">Penetration Testing & Auditing</option>
                          <option value="Zero Trust & Endpoint Security">Zero Trust & Endpoint Security</option>
                          <option value="Compliance & Regulatory Readiness">Compliance & Regulatory Readiness</option>
                        </select>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-600/30 hover:shadow-orange-600/40 mt-2"
                      >
                        Request Security Audit
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}